import { useEffect, useMemo, useRef, useState } from "react"
import { IFinalExamination } from "./types"
import { IFinalExaminationList } from "@api/finalExamination/state.types"
import { useDeviceType } from "@hooks/useDeviceType"
import { ComponentsHeader } from "@primitive/sectionHeadings"
import { Collapse, Popover } from 'antd'

import imgEmty from '@img/empty.svg'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import { 
    IconArchive, IconContracts, IconJobs, IconListeners, IconPrint
} from "@img/icons"

import { Button, Container, Empty, Flex, Loader, Pagination, Table, ToolTip } from "@ui/index"

import css from './ui.module.scss'


/** Иконка три вертикальные точки */
const IconDotsVertical = ({ width = '20px', height = '20px' }: { width?: string; height?: string }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="2"/>
        <circle cx="12" cy="12" r="2"/>
        <circle cx="12" cy="19" r="2"/>
    </svg>
)

/** Парсинг даты из строки DD.MM.YYYY */
const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null
    const [day, month, year] = dateStr.split('.')
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
}

/** Номер недели ISO 8601 */
const getWeekKey = (date: Date): string => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
    return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

/** Пн и Пт недели по её ключу */
const getWeekRange = (weekKey: string): { monday: Date; friday: Date } | null => {
    const [, weekStr] = weekKey.split('-W')
    const week = parseInt(weekStr)
    if (isNaN(week)) return null
    const jan4 = new Date(parseInt(weekKey), 0, 4)
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1 + (week - 1) * 7)
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    return { monday, friday }
}

/** Форматирование даты для заголовка */
const formatDateShort = (date: Date): string => {
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    return `${weekdays[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

/** Очередность статусов внутри недели: зелёный → жёлтый → красный */
const getStatusSortOrder = (item: IFinalExaminationList): number => {
    if (item.toPrint === 1) return 0
    if (item.defaulColorStatus === '#b69a00') return 1
    if (item.defaulColorStatus === '#ff0000' || item.defaulColorStatus === '#303B44') return 2
    return 3
}

const getSiteLogo = (dataBase: string) => {
    if (dataBase === 'yaskill') return { src: logoYaSkill, alt: 'Логотип YaSkill' }
    if (dataBase === 'modern_academy') return { src: logoModSkill, alt: 'Логотип ModSkill' }
    return null
}


/** Итоговые работы */
export const FinalExamination = ({
    isLoading, txtSearch, countUnverifiedWorks, loadingTitle, currentPageNumber, totalCount, pageSize, listenersList, 
    loadDataListeners, txtSearchOnChange, handlerOpenModalConfirm, handleArchiveWeek, handleOpenCardContract, handleOpenCardListener, 
    handleOpenCardFinalExamination, handleOpenFinalExaminationJobs, 
}: IFinalExamination) => {
    const deviceType = useDeviceType()
    const actionIconSize = deviceType === 'mobile' ? '16px' : '20px'

    /** Группировка списка по неделям */
    const weekGroups = useMemo(() => {
        const groups = new Map<string, { items: IFinalExaminationList[]; green: number; yellow: number; red: number }>()

        for (const item of listenersList) {
            const date = item.dateEducationEnd ? parseDate(item.dateEducationEnd) : null
            const key = date ? getWeekKey(date) : '__no_date__'
            if (!groups.has(key)) groups.set(key, { items: [], green: 0, yellow: 0, red: 0 })
            const group = groups.get(key)!
            group.items.push(item)
            if (item.toPrint === 1) group.green++
            else if (item.defaulColorStatus === '#b69a00') group.yellow++
            else if (item.defaulColorStatus === '#ff0000' || item.defaulColorStatus === '#303B44') group.red++
        }

        const entries = Array.from(groups.entries())
        entries.sort(([a], [b]) => {
            if (a === '__no_date__') return 1
            if (b === '__no_date__') return -1
            return a.localeCompare(b)
        })

        entries.forEach(([, group]) => {
            group.items.sort((a, b) => getStatusSortOrder(a) - getStatusSortOrder(b))
        })

        return entries
    }, [listenersList])

    /** Ключ текущей недели */
    const currentWeekKey = useMemo(() => getWeekKey(new Date()), [])

    /** Все ключи групп */
    const allWeekKeys = useMemo(() => weekGroups.map(([key]) => key), [weekGroups])

    /** Неделя, которая должна быть открыта и выделена как текущая */
    const highlightedWeekKey = useMemo(() => {
        const datedWeekKeys = weekGroups.map(([key]) => key).filter((key) => key !== '__no_date__')
        if (datedWeekKeys.includes(currentWeekKey)) return currentWeekKey
        return datedWeekKeys.find((key) => key > currentWeekKey) || ''
    }, [weekGroups, currentWeekKey])

    /** Управляемое состояние активных панелей Collapse */
    const [activeKeys, setActiveKeys] = useState<string[]>([])
    const didInit = useRef(false)
    const prevHighlightedWeekKey = useRef(highlightedWeekKey)

    /** Первая загрузка: открыть текущую неделю или следующую за ней */
    useEffect(() => {
        if (didInit.current) return
        if (weekGroups.length > 0) {
            didInit.current = true
            prevHighlightedWeekKey.current = highlightedWeekKey
            if (highlightedWeekKey) {
                setActiveKeys([highlightedWeekKey])
            }
        }
    }, [weekGroups, highlightedWeekKey])

    /** После перезагрузки данных держать открытой выделенную неделю, если старая исчезла */
    useEffect(() => {
        if (!didInit.current || txtSearch.value) return

        const previousHighlightedWeekKey = prevHighlightedWeekKey.current
        const isHighlightedWeekChanged = previousHighlightedWeekKey !== highlightedWeekKey
        prevHighlightedWeekKey.current = highlightedWeekKey

        if (!highlightedWeekKey) return
        if (!isHighlightedWeekChanged) return
        if (activeKeys.length === 1 && activeKeys[0] === highlightedWeekKey) return

        const hasOpenExistingWeek = activeKeys.some((key) => allWeekKeys.includes(key))
        if (!hasOpenExistingWeek || activeKeys.includes(previousHighlightedWeekKey)) {
            setActiveKeys([highlightedWeekKey])
        }
    }, [highlightedWeekKey, txtSearch.value, allWeekKeys])

    /** При изменении поиска или данных: раскрыть всё при поиске, свернуть до выделенной недели при очистке */
    const prevSearch = useRef(txtSearch.value)
    useEffect(() => {
        if (!didInit.current) return

        const wasSearching = !!prevSearch.current
        const isSearching = !!txtSearch.value
        prevSearch.current = txtSearch.value

        if (isSearching) {
            setActiveKeys(allWeekKeys)
        } else if (wasSearching && !isSearching) {
            setActiveKeys(highlightedWeekKey ? [highlightedWeekKey] : [])
        }
    }, [txtSearch.value, highlightedWeekKey, allWeekKeys])

    return <div className={css['container']}>

        <ComponentsHeader
            headerIcon={<IconJobs width="20px" height="20px" />}
            headerTitle="Итоговые работы"
            txtSearch={txtSearch.value}
            txtSearchTooltip={<>Поиск по:<br />✓ Фамилии<br />✓ Имени<br />✓ Отчеству<br />✓ Телефону<br />✓ Email<br />✓ СНИЛС<br />✓ Номеру договора<br />✓ Идентификатору заявки<br />✓ Наименованию курса</>}
            txtSearchOnChange={(e) => txtSearchOnChange(e.target.value)}
            txtSearchClearOnClick={() => txtSearchOnChange('')}
            btnUpdateOnClick={() => loadDataListeners(txtSearch.value)}
            btnJobsOnClick={() => handleOpenFinalExaminationJobs(true)}
            btnJobsCount={countUnverifiedWorks}
        />

        <Table.Container>
            <Loader isOpen={isLoading} backdrop color="var(--foreground_accent_default)" title={loadingTitle.value} />
            <Table.Header paddingLeft="52px" paddingRight="20px" classNameContainer={css['hidden_666']} classNameContent={css['table_grid_columns_header']}>
                <p style={{ textAlign: 'center' }}>№<br/>заявки</p>
                <p>ФЛ / ЮЛ / ФИО слушателя<br />Документ / Атте-я / Долг / ИАР / Тест</p>
                <p className={css['hidden_940']}>Программа<br />Тема итоговой работы</p>
                <p className={css['hidden_1200']} style={{ textAlign: 'center' }}>№ и дата<br />договора</p>
                <p className={css['hidden_1080']} style={{ textAlign: 'center' }}>Начало / конец<br />обучения</p>
                <p style={{ textAlign: 'center' }}>Действия</p>
            </Table.Header>

            <Table.Content>
                {listenersList.length === 0
                    ? <Empty image={imgEmty} title='Данные отсутствуют' />
                    : (
                        <Collapse
                            activeKey={activeKeys}
                            onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                            className={css['date_collapse']}
                            expandIconPosition="end"
                            size="small"
                        >
                            {weekGroups.map(([weekKey, group]) => {
                                const isNoDate = weekKey === '__no_date__'
                                const range = isNoDate ? null : getWeekRange(weekKey)
                                const formattedDate = isNoDate ? 'Без даты' : range ? `${formatDateShort(range.monday)} → ${formatDateShort(range.friday)}` : weekKey
                                const isHighlightedWeek = weekKey === highlightedWeekKey

                                return (
                                    <Collapse.Panel
                                        key={weekKey}
                                        className={isHighlightedWeek ? css['panel_closest'] : ''}
                                        header={
                                            <span className={css['collapse_header']}>
                                                <span
                                                    className={css['collapse_archive_button']}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ToolTip title='Переместить неделю в архив' placement="bottomLeft">
                                                        <Button
                                                            variant="icon"
                                                            size="xs"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                e.stopPropagation()
                                                                handleArchiveWeek(formattedDate, group.items)
                                                            }}
                                                        >
                                                            <IconArchive width="16px" height="16px" />
                                                        </Button>
                                                    </ToolTip>
                                                </span>
                                                <span className={`${css['collapse_date']} ${isHighlightedWeek ? css['collapse_date_closest'] : ''}`}>{formattedDate}</span>
                                                <span className={css['collapse_counts']}>
                                                    {group.red > 0 && <span className={`${css['count_badge']} ${css['count_red']}`}>{group.red}</span>}
                                                    {group.yellow > 0 && <span className={`${css['count_badge']} ${css['count_yellow']}`}>{group.yellow}</span>}
                                                    {group.green > 0 && <span className={`${css['count_badge']} ${css['count_green']}`}>{group.green}</span>}
                                                </span>
                                            </span>
                                        }
                                    >
                                        <Flex gap="0px" flexDirection="column">
                                            {group.items.map((item) => {
                                                const fullName = [item.lastName, item.firstName, item.secondName].filter(Boolean).join(' ')
                                                const siteLogo = getSiteLogo(item.dataBase)
                                                const recordBadgeBackground = item.contractStatusID === 6
                                                    ? 'linear-gradient(180deg, #f34e2e 0%, #8a2312 100%)'
                                                    : item.defaulColorStatus

                                                return (
                                                    <Table.Row
                                                        key={item.finalExaminationID}
                                                        className={css['table_grid_columns_content']}
                                                        onClick={() => handleOpenCardFinalExamination(true, item.finalExaminationID, fullName, item.attestationTypeName, item.dataBase)}
                                                    >
                                <span className={css['site_logo_cell']}>
                                    {siteLogo && <img className={css['site_logo']} src={siteLogo.src} alt={siteLogo.alt} />}
                                </span>

                                <div  className={css['record_badge']} style={{ background: recordBadgeBackground }}>
                                    <p>{item.applicationID}</p>
                                </div>

                                <div className={css['record_primary']}>
                                    <p>{item.contractTypeName} 👉 {fullName}</p>
                                    <p className={css['record_secondary_text']}>
                                        {item.documentTypeName} / {item.attestationTypeName} / <b style={{ color: item.dutyPayment > 0 ? '#d13232' : 'inherit' }}>{item.dutyPayment}</b> / <span style={{ color: item.attestationTypeName === 'ВР' && !item.scoreIAR ? '#d13232' : 'inherit' }}>{item.scoreIAR || 0}</span> / <span style={{ color: item.attestationTypeName === 'ИТ' && !item.scoreTest ? '#d13232' : 'inherit' }}>{item.scoreTest || 0}</span>
                                    </p>
                                </div>

                                <div className={css['hidden_940']}>
                                    <ToolTip title={item.themeIAR || 'Тема итоговой работы не указана'} placement="topLeft">
                                        <div className={css['record_primary']}>
                                            <p>{item.courseName}</p>
                                            <p className={css['record_secondary_text']}>{item.themeIAR || '----'}</p>
                                        </div>
                                    </ToolTip>
                                </div>

                                <div className={`${css['record_dates']} ${css['hidden_1200']}`.trim()}>
                                    <p>{item.contractNumber}</p>
                                    <p className={css['record_secondary_text']}>{item.contractDate}</p>
                                </div>

                                <div className={`${css['record_dates']} ${css['hidden_1080']}`.trim()}>
                                    <p>{item.dateEducationStart}</p>
                                    <p className={css['record_secondary_text']} style={{ color: item.warningEndEducationDate === 1 ? '#d13232' : 'inherit', fontWeight: item.warningEndEducationDate === 1 ? 700 : 500 }}>{item.dateEducationEnd}</p>
                                </div>

                                <Flex gap="2px" flexDirection="row" alignItems="center" justifyContent="center" className={css['record_actions']}>
                                    {deviceType === 'mobile' ? (
                                        <Popover
                                            trigger="click"
                                            placement="bottomRight"
                                            content={
                                                <Flex gap="4px" flexDirection="row" alignItems="center">
                                                    <ToolTip title={item.toPrint === 0 ? 'Печать недоступна' : 'Отправить в печать'} placement="top">
                                                        <Button
                                                            variant="icon"
                                                            size="xs"
                                                            isDisabled={item.toPrint === 0}
                                                            onClick={(e) => {
                                                                handlerOpenModalConfirm(true, 'На печать', `Вы действительно хотите отправить в печать следующего слушателя:<br />👉 ${fullName};<br />👉 Договор № ${item.contractNumber}.`, 'toPrint', item.finalExaminationID)
                                                                e.stopPropagation()
                                                            }}
                                                        >
                                                            <IconPrint width="16px" height="16px" />
                                                        </Button>
                                                    </ToolTip>

                                                    <ToolTip title='Анкета договора' placement="top">
                                                        <Button
                                                            variant="icon"
                                                            size="xs"
                                                            onClick={(e) => {
                                                                handleOpenCardContract(true, item.contractID)
                                                                e.stopPropagation()
                                                            }}
                                                        >
                                                            <IconContracts width="16px" height="16px" />
                                                        </Button>
                                                    </ToolTip>

                                                    <ToolTip title='Анкета слушателя' placement="top">
                                                        <Button
                                                            variant="icon"
                                                            size="xs"
                                                            onClick={(e) => {
                                                                handleOpenCardListener(true, item.listenerID)
                                                                e.stopPropagation()
                                                            }}
                                                        >
                                                            <IconListeners width="16px" height="16px" />
                                                        </Button>
                                                    </ToolTip>

                                                    <ToolTip title='Переместить в архив' placement="top">
                                                        <Button
                                                            variant="icon"
                                                            size="xs"
                                                            onClick={(e) => {
                                                                handlerOpenModalConfirm(true, 'Отправка в архив', `Вы действительно хотите отправить в архив следующего слушателя:<br />👉 ${fullName};<br />👉 Договор № ${item.contractNumber}.`, 'inArchive', item.finalExaminationID)
                                                                e.stopPropagation()
                                                            }}
                                                        >
                                                            <IconArchive width="16px" height="16px" />
                                                        </Button>
                                                    </ToolTip>
                                                </Flex>
                                            }
                                        >
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <Button variant="icon" size="xs">
                                                    <IconDotsVertical width="16px" height="16px" />
                                                </Button>
                                            </div>
                                        </Popover>
                                    ) : (
                                        <>
                                            <ToolTip title={item.toPrint === 0 ? 'Печать недоступна' : 'Отправить в печать'} placement="bottomRight">
                                                <Button
                                                    variant="icon"
                                                    size="sm"
                                                    isDisabled={item.toPrint === 0}
                                                    onClick={(e) => {
                                                        handlerOpenModalConfirm(true, 'На печать', `Вы действительно хотите отправить в печать следующего слушателя:<br />👉 ${fullName};<br />👉 Договор № ${item.contractNumber}.`, 'toPrint', item.finalExaminationID)
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <IconPrint width={actionIconSize} height={actionIconSize} />
                                                </Button>
                                            </ToolTip>

                                            <ToolTip title='Анкета договора' placement="bottomRight">
                                                <Button
                                                    variant="icon"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        handleOpenCardContract(true, item.contractID)
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <IconContracts width={actionIconSize} height={actionIconSize} />
                                                </Button>
                                            </ToolTip>

                                            <ToolTip title='Анкета слушателя' placement="bottomRight">
                                                <Button
                                                    variant="icon"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        handleOpenCardListener(true, item.listenerID)
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <IconListeners width={actionIconSize} height={actionIconSize} />
                                                </Button>
                                            </ToolTip>

                                            <ToolTip title='Переместить в архив' placement="bottomRight">
                                                <Button
                                                    variant="icon"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        handlerOpenModalConfirm(true, 'Отправка в архив', `Вы действительно хотите отправить в архив следующего слушателя:<br />👉 ${fullName};<br />👉 Договор № ${item.contractNumber}.`, 'inArchive', item.finalExaminationID)
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <IconArchive width={actionIconSize} height={actionIconSize} />
                                                </Button>
                                            </ToolTip>
                                        </>
                                    )}
                                </Flex>
                            </Table.Row>
                                                )
                                            })}
                                        </Flex>
                                    </Collapse.Panel>
                                )
                            })}
                        </Collapse>
                    )
                }
            </Table.Content>
        </Table.Container>

        <Container padding="6px" className={css['footer']} overflow="visible">
            <Flex gap="4px" flexDirection="row" alignItems="center">
                <ToolTip title='Общее количество' placement="topLeft">
                    <p className={css['stat_all']}>{totalCount}</p>
                </ToolTip>
            </Flex>

            <Pagination
                totalCount={totalCount}
                pageSize={pageSize}
                currentPage={currentPageNumber.value}
                onChange={(e) => currentPageNumber.setValue(e)}
            />
        </Container>
    </div>
}